/**
 * Téléchargement direct d'un fichier, sans ouvrir d'onglet.
 *
 * Le motif naïf — une ancre `<a href={url} download>` que l'on clique — échoue
 * de deux façons, et les deux se voyaient dans l'application :
 *
 *   1. L'attribut `download` est IGNORÉ quand l'URL vient d'une AUTRE ORIGINE
 *      (règle du navigateur, pas un bogue). Le clic devient une navigation :
 *      l'image s'ouvre dans un onglet, à charge pour l'utilisateur de faire un
 *      clic droit « Enregistrer sous ». C'est exactement le symptôme signalé
 *      sur les images du chat.
 *   2. Une ancre jamais insérée dans le document ne reçoit pas toujours le
 *      clic : Chrome l'accepte, Firefox l'ignore silencieusement.
 *
 * On passe donc toujours par un Blob, qui devient une URL `blob:` de MÊME
 * origine — là, `download` est honoré — et l'ancre est réellement insérée dans
 * le document avant d'être cliquée puis retirée.
 */

function triggerAnchor(href, filename) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.rel = 'noopener';
  // Insertion réelle : cf. point 2 ci-dessus.
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Décode une URL `data:` en Blob sans passer par le réseau.
 *
 * `fetch()` sait lire une URL `data:`, mais pas partout : Safari a longtemps
 * échoué au-delà de quelques mégaoctets, et nos images en 4K les dépassent.
 * Le décodage manuel n'a pas cette limite.
 */
function dataUrlToBlob(dataUrl) {
  const virgule = dataUrl.indexOf(',');
  if (virgule === -1) throw new Error('URL data: malformée');
  const entete = dataUrl.slice(5, virgule); // après « data: »
  const type = entete.split(';')[0] || 'application/octet-stream';
  const charge = dataUrl.slice(virgule + 1);

  if (!/;base64/i.test(entete)) {
    return new Blob([decodeURIComponent(charge)], { type });
  }

  const binaire = atob(charge);
  // Découpage en tranches : un Uint8Array construit caractère par caractère sur
  // plusieurs mégaoctets fige le fil principal.
  const tranches = [];
  const TAILLE = 8192;
  for (let debut = 0; debut < binaire.length; debut += TAILLE) {
    const morceau = binaire.slice(debut, debut + TAILLE);
    const octets = new Uint8Array(morceau.length);
    for (let i = 0; i < morceau.length; i++) octets[i] = morceau.charCodeAt(i);
    tranches.push(octets);
  }
  return new Blob(tranches, { type });
}

/**
 * Télécharge `url` sous le nom `filename`, dans le dossier de téléchargement
 * du navigateur, sans ouvrir d'onglet.
 *
 * Accepte une URL `data:`, `blob:` ou http(s). Renvoie `true` si le fichier est
 * bien parti en tant que Blob, `false` si le repli dégradé a dû servir — un
 * appelant qui veut prévenir l'utilisateur peut lire ce retour.
 */
export async function downloadUrl(url, filename) {
  if (!url) return false;

  let objectUrl = null;
  try {
    const blob = url.startsWith('data:')
      ? dataUrlToBlob(url)
      : await fetch(url, { mode: 'cors', credentials: 'omit' }).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.blob();
        });
    objectUrl = URL.createObjectURL(blob);
    triggerAnchor(objectUrl, filename);
    return true;
  } catch (e) {
    // Repli : une URL d'une autre origine servie sans en-têtes CORS ne peut pas
    // être lue en Blob. On retombe sur l'ancre directe — le navigateur ouvrira
    // peut-être un onglet, ce qui est dégradé mais reste mieux que rien.
    console.warn('Téléchargement en Blob impossible, repli sur le lien direct:', e);
    triggerAnchor(url, filename);
    return false;
  } finally {
    // Révocation DIFFÉRÉE : révoquer tout de suite annule le téléchargement en
    // cours sur Safari, qui n'a pas encore lu le Blob quand le clic revient.
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
  }
}

/** Variante pour un Blob déjà en main (export ZIP, vectorisation…). */
export function downloadBlob(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  triggerAnchor(objectUrl, filename);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
}
